import { useDataGrid, List, ShowButton, EditButton } from '@refinedev/mui'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import {
  Typography,
  IconButton,
  Link,
  Box,
  Button,
  Chip,
  Autocomplete,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from '@mui/material'

import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'

import { useList, useGetIdentity, useOne, useGo } from '@refinedev/core'
import { User } from '@/utils/auth/auth0'
import { useState, useMemo, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import AssignUserOpportunityModal from '../../components/AssignUserOpportunityModal'
import OpportunitiesQuickSearchNew from '../../components/OpportunitiesQuickSearchNew'
import { PaginationControls } from '../../components/PaginationControls'
import { UUID } from 'crypto'

interface OpportunityTag {
  key: string // Enum key like "ARTS_CREATIVITY"
  value: string // Display value like "Arts & Creativity"
  name: string
  description: string
}

export const OpportunitiesList: React.FC = () => {
  const user = useGetIdentity().data as User
  const location = useLocation()
  const go = useGo()

  const [assignUserModalOpen, setAssignUserModalOpen] = useState(false)
  const [selectedOpportunity, setSelectedOpportunity] = useState<UUID | null>(
    null,
  )
  const [rowCurrentAssignee, setRowCurrentAssignee] = useState<string | null>(
    null,
  )

  // Initialize selectedTags from URL - handle indexed params (tags[0], tags[1], etc.)
  const [selectedTags, setSelectedTags] = useState<string[]>(() => {
    const params = new URLSearchParams(location.search)
    const tags: string[] = []
    let index = 0
    while (params.has(`tags[${index}]`)) {
      const tag = params.get(`tags[${index}]`)
      if (tag) tags.push(tag)
      index++
    }
    return tags
  })

  // Initialize tag match mode from URL
  const [tagMatchMode, setTagMatchMode] = useState<'any' | 'all' | 'exact'>(
    () => {
      const params = new URLSearchParams(location.search)
      return (params.get('tag_match_mode') as 'any' | 'all' | 'exact') || 'any'
    },
  )

  // Helper function to update URL with tag filters
  const updateUrlWithTagFilters = (
    newTags: string[],
    newMode: 'any' | 'all' | 'exact',
  ) => {
    const currentParams = new URLSearchParams(location.search)
    const query: any = {}

    // Build new query object, excluding tag-related params
    currentParams.forEach((value, key) => {
      // Skip tag-related params (tags, tags[0], tags[1], etc.)
      if (
        key === 'tags' ||
        key.startsWith('tags[') ||
        key === 'tag_match_mode'
      ) {
        return
      }
      if (query[key]) {
        // Handle multiple values for same key
        if (Array.isArray(query[key])) {
          query[key].push(value)
        } else {
          query[key] = [query[key], value]
        }
      } else {
        query[key] = value
      }
    })

    // Add fresh tags array
    if (newTags.length > 0) {
      query.tags = newTags
      query.tag_match_mode = newMode
    }

    go({
      to: {
        resource: 'opportunities',
        action: 'list',
      },
      query,
    })
  }

  // Fetch categories (tags)
  const { data: categories, isLoading: categoriesIsLoading } = useList({
    resource: 'opportunity_categories',
    config: {
      pagination: { current: 1, pageSize: 100 },
    },
    errorNotification: (error) => {
      return {
        message: 'Error getting categories',
        description:
          error?.message || 'An error occurred while fetching categories',
        type: 'error',
      }
    },
  })

  const categoriesList = useMemo(
    () => (categories?.data as OpportunityTag[]) || [],
    [categories?.data],
  )

  // Use enum keys for URL-safe filtering, but display values
  // Map of key->value for display
  const tagMap = useMemo(() => {
    const map: Record<string, string> = {}
    categoriesList.forEach((cat) => {
      map[cat.key] = cat.value
    })
    return map
  }, [categoriesList])

  const availableTags = useMemo(
    () =>
      categoriesList
        .filter((cat) => cat.key && cat.key.trim() !== '')
        .map((cat) => cat.key)
        .sort((a, b) => (tagMap[a] || '').localeCompare(tagMap[b] || '')),
    [categoriesList, tagMap],
  )

  const { data: userData, isLoading } = useOne({
    resource: 'user-by-email',
    id: user?.email,
    queryOptions: {
      enabled: !!user,
    },
  })

  // Build filters from URL parameters for server-side filtering
  const getFiltersFromURL = () => {
    const params = new URLSearchParams(location.search)
    const filters: any[] = []

    // Handle traffic light status filter
    const trafficLightStatus = params.get('traffic_light_status')
    if (trafficLightStatus) {
      filters.push({
        field: 'traffic_light_status',
        operator: 'eq',
        value: trafficLightStatus,
      })
    }

    // Handle flag_for_updates filter
    const flagForUpdates = params.get('flag_for_updates')
    if (flagForUpdates) {
      filters.push({
        field: 'flag_for_updates',
        operator: 'eq',
        value: flagForUpdates,
      })
    }

    // Handle quick search - for now, just search fund_name
    // TODO: Backend needs to support OR operator for searching multiple fields
    const quickSearch = params.get('q')
    if (quickSearch) {
      filters.push({
        field: 'fund_name',
        operator: 'contains',
        value: quickSearch,
      })
    }

    // Handle assigned to filter
    const assignedTo = params.get('assigned_to')
    if (assignedTo === 'unassigned') {
      filters.push({
        field: 'assigned_to_user_uuid',
        operator: 'null',
        value: true,
      })
    } else if (assignedTo && assignedTo !== '[object Object]') {
      // Use the UUID directly (no longer storing JSON objects)
      filters.push({
        field: 'assigned_to_user_uuid',
        operator: 'eq',
        value: assignedTo,
      })
    }

    // Handle date range filters
    const dateStart = params.get('date_start')
    const dateEnd = params.get('date_end')
    if (dateStart) {
      filters.push({
        field: 'next_check_in_date',
        operator: 'gte',
        value: dateStart,
      })
    }
    if (dateEnd) {
      filters.push({
        field: 'next_check_in_date',
        operator: 'lte',
        value: dateEnd,
      })
    }

    // Handle no check-in date filter
    const noCheckInDate = params.get('no_check_in_date')
    if (noCheckInDate === 'true') {
      filters.push({
        field: 'next_check_in_date',
        operator: 'null',
        value: true,
      })
    }

    // Handle funding rounds filters
    const noFundingRounds = params.get('no_funding_rounds')
    const alwaysOpen = params.get('always_open')
    const fundingStartDate = params.get('funding_start_date')
    const fundingEndDate = params.get('funding_end_date')

    if (noFundingRounds === 'true') {
      filters.push({
        field: 'has_funding_rounds',
        operator: 'null',
        value: true,
      })
    }

    if (alwaysOpen === 'true') {
      filters.push({
        field: 'always_open',
        operator: 'eq',
        value: true,
      })
    }

    if (fundingStartDate) {
      filters.push({
        field: 'application_open_date',
        operator: 'gte',
        value: fundingStartDate,
      })
    }

    if (fundingEndDate) {
      filters.push({
        field: 'application_open_date',
        operator: 'lte',
        value: fundingEndDate,
      })
    }

    const openNow = params.get('open_now')
    if (openNow === 'true') {
      filters.push({
        field: 'open_now',
        operator: 'eq',
        value: true,
      })
    }

    const pastOnly = params.get('past_only')
    if (pastOnly === 'true') {
      filters.push({
        field: 'past_only',
        operator: 'eq',
        value: true,
      })
    }

    // Handle is_international filter
    const isInternational = params.get('is_international')
    if (isInternational === 'true') {
      filters.push({
        field: 'is_international',
        operator: 'eq',
        value: true,
      })
    } else if (isInternational === 'false') {
      filters.push({
        field: 'is_international',
        operator: 'eq',
        value: false,
      })
    }

    // Handle questions_last_checked_at filters (stale-days + date range)
    const questionsStaleDays = params.get('questions_stale_days')
    if (questionsStaleDays && Number(questionsStaleDays) > 0) {
      const cutoff = new Date(
        Date.now() - Number(questionsStaleDays) * 86400000,
      )
        .toISOString()
        .slice(0, 10)
      filters.push({
        field: 'questions_last_checked_at',
        // Never-checked (NULL) is staler than any cutoff, so it belongs in a
        // staleness filter (GH #2217). A plain `lte` would hide exactly the
        // rows this filter exists to surface. The explicit date-range filters
        // below stay `gte`/`lte` — a NULL is genuinely not in a date range.
        operator: 'lte_or_null',
        value: cutoff,
      })
    }
    const questionsCheckedStart = params.get('questions_checked_start')
    const questionsCheckedEnd = params.get('questions_checked_end')
    if (questionsCheckedStart) {
      filters.push({
        field: 'questions_last_checked_at',
        operator: 'gte',
        value: questionsCheckedStart,
      })
    }
    if (questionsCheckedEnd) {
      filters.push({
        field: 'questions_last_checked_at',
        operator: 'lte',
        value: questionsCheckedEnd,
      })
    }

    // Handle region filter
    const regionId = params.get('region_id')
    if (regionId === 'no_region') {
      filters.push({
        field: 'region_id',
        operator: 'null',
        value: true,
      })
    } else if (regionId && regionId !== '[object Object]') {
      // Parse region ID if it's a JSON object from UnifiedQuickSearch
      let actualRegionId = regionId
      let isNoRegion = false
      try {
        const regionObj = JSON.parse(regionId)
        if (regionObj && regionObj.value && regionObj.value.id) {
          actualRegionId = regionObj.value.id
          // Handle the "None" option which has id 'no_region'
          if (actualRegionId === 'no_region') {
            isNoRegion = true
          }
        } else if (regionObj && regionObj.id) {
          // Fallback for older format
          actualRegionId = regionObj.id
          if (actualRegionId === 'no_region') {
            isNoRegion = true
          }
        }
      } catch {
        // If parsing fails, use the original value
        actualRegionId = regionId
      }

      if (isNoRegion) {
        filters.push({
          field: 'region_id',
          operator: 'null',
          value: true,
        })
      } else {
        filters.push({
          field: 'region_id',
          operator: 'eq',
          value: actualRegionId,
        })
      }
    }

    // Handle profile filter
    const profileFilter = params.get('profile')
    if (profileFilter === 'true') {
      filters.push({
        field: 'profile_text',
        operator: 'nnull',
        value: true,
      })
    } else if (profileFilter === 'false') {
      filters.push({
        field: 'profile_text',
        operator: 'null_or_empty',
        value: true,
      })
    }

    // Handle inherit regions from funder filter
    const inheritFromFunder = params.get('inherit_regions_from_funder')
    if (inheritFromFunder === 'true') {
      const regionId = params.get('region_id')
      let isNoRegion = false
      let actualRegionId = regionId

      // Parse the region ID to check if it's "no_region"
      if (regionId) {
        try {
          const regionObj = JSON.parse(regionId)
          if (regionObj && regionObj.value && regionObj.value.id) {
            actualRegionId = regionObj.value.id
            if (actualRegionId === 'no_region') {
              isNoRegion = true
            }
          } else if (regionObj && regionObj.id) {
            actualRegionId = regionObj.id
            if (actualRegionId === 'no_region') {
              isNoRegion = true
            }
          }
        } catch {
          // If parsing fails, check if it's the string 'no_region'
          if (regionId === 'no_region') {
            isNoRegion = true
          }
        }
      }

      if (isNoRegion) {
        // "None" region selected with inherit from funder - show opportunities where the funder also has no region
        // This creates an additional restriction: both opportunity AND funder must have no region
        filters.push({
          field: 'inherit_regions_from_funder',
          operator: 'null',
          value: true,
        })
      } else if (actualRegionId && actualRegionId !== 'no_region') {
        // Specific region selected - show opportunities inheriting that region from funder
        filters.push({
          field: 'inherit_regions_from_funder',
          operator: 'eq',
          value: actualRegionId,
        })
      } else {
        // No region filter selected - show opportunities from funders with any regions
        filters.push({
          field: 'inherit_regions_from_funder',
          operator: 'nnull',
          value: true,
        })
      }
    }

    // Handle within region filter
    const withinRegion = params.get('within_region')
    if (withinRegion === 'true') {
      const regionId = params.get('region_id')
      if (regionId && regionId !== 'no_region') {
        filters.push({
          field: 'within_region',
          operator: 'eq',
          value: regionId,
        })
      }
    }

    // Handle computed flag filters
    if (params.get('has_current_funding_rounds') === 'true') {
      filters.push({
        field: 'has_current_funding_rounds',
        operator: 'eq',
        value: 'true',
      })
    }

    if (params.get('needs_funding_rounds') === 'true') {
      filters.push({
        field: 'needs_funding_rounds',
        operator: 'eq',
        value: 'true',
      })
    }

    if (params.get('has_expired_rounds') === 'true') {
      filters.push({
        field: 'has_expired_rounds',
        operator: 'eq',
        value: 'true',
      })
    }

    // Handle tag filter - extract array from indexed params (tags[0], tags[1], etc.)
    const tags: string[] = []
    let index = 0
    while (params.has(`tags[${index}]`)) {
      const tag = params.get(`tags[${index}]`)
      if (tag) tags.push(tag)
      index++
    }

    const tagMatchMode = params.get('tag_match_mode') || 'any'
    if (tags.length > 0) {
      filters.push({
        field: 'tags',
        operator: 'contains',
        value: { tags, mode: tagMatchMode },
      })
    }

    return filters
  }

  const { dataGridProps, current, setCurrent, pageSize, setPageSize } =
    useDataGrid({
      resource: 'opportunities',
      filters: {
        permanent: getFiltersFromURL(),
      },
      sorters: {
        initial: [
          {
            field: 'next_check_in_date',
            order: 'desc',
          },
        ],
      },
      errorNotification: (error) => {
        return {
          message: 'Error getting opportunities',
          description:
            error?.message || 'An error occurred while fetching opportunities',
          type: 'error',
        }
      },
    })

  const { data: opportunityAssignee, isLoading: isLoadingOpportunityAssignee } =
    useList({
      resource: 'assigned-opportunities',

      queryOptions: {
        enabled: !!user,
      },
      errorNotification: (error) => {
        return {
          message: 'Error getting opportunity assignees',
          description:
            error?.message ||
            'An error occurred while fetching opportunity assignees',
          type: 'error',
        }
      },
    })

  const columns: GridColDef[] = [
    {
      field: 'fund_name',
      headerName: 'Name',
      flex: 1,
      minWidth: 300,
      filterable: true,
      renderCell: function render({ row }) {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              py: 0.5,
              gap: 0.5,
              width: '100%',
              maxWidth: '100%',
            }}
          >
            <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
              {row.fund_name}
            </Typography>
            {row.funder_name && (
              <Chip
                label={row.funder_name}
                size="small"
                onClick={(e) => {
                  e.stopPropagation() // Prevent row click
                  go({
                    to: {
                      resource: 'funders',
                      action: 'show',
                      id: row.funder_uuid,
                    },
                  })
                }}
                sx={{
                  height: '16px',
                  fontSize: '10px',
                  backgroundColor: '#f5f5f5',
                  color: '#666',
                  alignSelf: 'flex-start',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  },
                  '& .MuiChip-label': {
                    px: 0.5,
                    py: 0,
                  },
                }}
              />
            )}
            {row.tags && row.tags.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  overflowX: 'auto',
                  width: '100%',
                  minWidth: 0,
                  '&::-webkit-scrollbar': {
                    height: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    borderRadius: '4px',
                  },
                }}
              >
                {row.tags.map((tag: string) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    sx={{
                      height: '16px',
                      fontSize: '9px',
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                      flexShrink: 0,
                      '& .MuiChip-label': {
                        px: 0.5,
                        py: 0,
                      },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )
      },
    },
    {
      field: 'traffic_light_status',
      headerName: 'Status',
      flex: 0.2,
      minWidth: 70,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: function render({ row }) {
        const colorMap: Record<string, string> = {
          green: '#4caf50',
          yellow: '#ff9800',
          red: '#f44336',
        }
        const color = colorMap[row.traffic_light_status] ?? '#9e9e9e'
        return <FiberManualRecordIcon sx={{ color, fontSize: 28 }} />
      },
    },

    {
      field: 'flag_for_updates',
      headerName: 'Flag',
      sortable: true,
      filterable: true,
      valueGetter: (params) => {
        if (!params.row.flag_for_updates) return ''
        return params.row.flag_for_updates
      },
      renderCell: function render({ row }) {
        if (!row.flag_for_updates) return null
        return <Typography>{row.flag_for_updates || ''}</Typography>
      },
      minWidth: 150,
      flex: 1,
    },
    {
      field: 'next_check_in_date',
      headerName: 'Next Check-in',
      sortable: true,
      filterable: true,
      type: 'date',
      valueGetter: (params) => {
        return params.row.next_check_in_date
          ? new Date(params.row.next_check_in_date)
          : null
      },
      renderCell: function render({ row }) {
        return (
          <Typography>
            {row.next_check_in_date
              ? new Date(row.next_check_in_date).toLocaleDateString('en-NZ', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
              : ''}
          </Typography>
        )
      },
      minWidth: 120,
      align: 'center',
      flex: 1,
    },
    {
      field: 'questions_last_checked_at',
      headerName: 'Questions Checked',
      sortable: true,
      filterable: true,
      type: 'date',
      valueGetter: (params) => {
        return params.row.questions_last_checked_at
          ? new Date(params.row.questions_last_checked_at)
          : null
      },
      renderCell: function render({ row }) {
        return (
          <Typography>
            {row.questions_last_checked_at
              ? new Date(row.questions_last_checked_at).toLocaleDateString(
                  'en-NZ',
                  {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  },
                )
              : 'Never'}
          </Typography>
        )
      },
      minWidth: 130,
      align: 'center',
      flex: 1,
    },
    {
      field: 'assigned_to',
      headerName: 'Assigned To',
      flex: 1,
      sortable: false,
      minWidth: 80,
      maxWidth: 200,
      // Computed client-side from a separate assignee lookup; the API has no
      // assigned_to column, so a header filter on it is rejected server-side.
      // Assignee filtering goes through the assigned_to URL param instead.
      filterable: false,
      valueGetter: (params) => {
        const assignedOpportunity = opportunityAssignee?.data.find(
          (oa) => oa.opportunity_uuid === params.row.uuid,
        )
        return assignedOpportunity
          ? assignedOpportunity.assigned_to_user
          : 'Unassigned'
      },
      renderCell: function render({ row }) {
        return (
          <Box
            key={row.uuid}
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {opportunityAssignee &&
              (() => {
                const assignedOpportunity = opportunityAssignee.data.find(
                  (opportunityAssignee) =>
                    opportunityAssignee.opportunity_uuid === row.uuid,
                )
                return assignedOpportunity ? (
                  <>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation() // Prevent row click
                        handleAddEditAssignee(
                          row.uuid,
                          assignedOpportunity.assigned_to_user_uuid,
                        )
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <Typography variant="body2">
                      {assignedOpportunity.assigned_to_user}
                    </Typography>
                  </>
                ) : (
                  <>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation() // Prevent row click
                        handleAddEditAssignee(row.uuid, null)
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                    <Typography variant="body2">Unassigned</Typography>
                  </>
                )
              })()}
          </Box>
        )
      },
    },
    {
      field: 'profile',
      headerName: 'Profile',
      flex: 0.25,
      minWidth: 75,
      align: 'center',
      headerAlign: 'center',
      // Derived from profile_text; the API has no profile column, so a header
      // filter on it is rejected server-side. Use the profile URL param, which
      // sends a profile_text null/not-null filter.
      filterable: false,
      type: 'boolean',
      valueGetter: (params) => {
        return params.row.profile_text && params.row.profile_text.trim() !== ''
      },
      renderCell: function render({ row }) {
        const hasProfile = row.profile_text && row.profile_text.trim() !== ''
        return (
          <Typography>
            {hasProfile ? (
              <CheckCircleIcon color="success" fontSize="inherit" />
            ) : (
              <CancelIcon color="error" fontSize="inherit" />
            )}
          </Typography>
        )
      },
    },

    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      renderCell: function render({ row }) {
        return (
          <Box onClick={(e) => e.stopPropagation()}>
            <ShowButton hideText recordItemId={row.uuid} />
            <EditButton hideText recordItemId={row.uuid} />
          </Box>
        )
      },

      maxWidth: 100,
    },
  ]

  function handleAddEditAssignee(row_uuid: UUID, assigned_to_user_uuid: null) {
    setSelectedOpportunity(row_uuid)
    setAssignUserModalOpen(true)
    setRowCurrentAssignee(assigned_to_user_uuid)
  }

  return (
    <>
      <Typography variant="h5" sx={{ mt: 1, mb: 3 }}>
        {user?.email && userData ? (
          <Button variant="contained" sx={{ mb: 2, color: 'white' }}>
            <Link
              href={`/opportunities/assigned/${userData.data}`}
              style={{ color: 'white' }}
            >
              Show what I'm assigned to
            </Link>
          </Button>
        ) : (
          'Loading...'
        )}
      </Typography>
      {assignUserModalOpen && (
        <AssignUserOpportunityModal
          open={assignUserModalOpen}
          onClose={() => setAssignUserModalOpen(false)}
          opportunity_uuid={selectedOpportunity}
          currentUser={user}
          currentAssignedUser={rowCurrentAssignee}
        />
      )}
      <List>
        <OpportunitiesQuickSearchNew />
        <Box sx={{ mb: 2 }}>
          <Autocomplete
            multiple
            options={availableTags}
            value={selectedTags}
            onChange={(_, newValue) => {
              setSelectedTags(newValue)
              updateUrlWithTagFilters(newValue, tagMatchMode)
            }}
            getOptionLabel={(option) => tagMap[option] || option}
            isOptionEqualToValue={(option, value) => option === value}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by tags"
                placeholder="Select tags..."
              />
            )}
          />
          {selectedTags.length > 0 && (
            <Box
              sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}
            >
              <FormControl>
                <FormLabel sx={{ mb: 1, fontWeight: 600 }}>
                  Tag Match Mode
                </FormLabel>
                <RadioGroup
                  row
                  value={tagMatchMode}
                  onChange={(e) => {
                    const newMode = e.target.value as 'any' | 'all' | 'exact'
                    setTagMatchMode(newMode)
                    updateUrlWithTagFilters(selectedTags, newMode)
                  }}
                >
                  {selectedTags.length === 1 ? (
                    <>
                      <FormControlLabel
                        value="any"
                        control={<Radio size="small" />}
                        label="Contains this tag"
                      />
                      <FormControlLabel
                        value="exact"
                        control={<Radio size="small" />}
                        label="Only this tag (and no others)"
                      />
                    </>
                  ) : (
                    <>
                      <FormControlLabel
                        value="any"
                        control={<Radio size="small" />}
                        label="Either of these tags"
                      />
                      <FormControlLabel
                        value="all"
                        control={<Radio size="small" />}
                        label="All of these tags (may have others)"
                      />
                      <FormControlLabel
                        value="exact"
                        control={<Radio size="small" />}
                        label="Only these tags (and no others)"
                      />
                    </>
                  )}
                </RadioGroup>
              </FormControl>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <PaginationControls
            currentPage={current}
            pageSize={pageSize}
            totalCount={dataGridProps.rowCount || 0}
            pageSizeOptions={[10, 25, 50, 100]}
            onPageChange={setCurrent}
            onPageSizeChange={setPageSize}
          />
        </Box>
        <DataGrid
          {...dataGridProps}
          columns={columns}
          autoHeight
          getRowId={(row) => row.uuid}
          disableColumnFilter={false}
          pageSizeOptions={[10, 25, 50, 100]}
          rowHeight={70}
          onRowClick={(params, event) => {
            // Check if cmd/ctrl key is pressed for opening in new tab
            if (event.metaKey || event.ctrlKey) {
              // Open in new tab
              window.open(`/opportunities/${params.row.uuid}`, '_blank')
            } else {
              // Normal navigation
              go({
                to: {
                  resource: 'opportunities',
                  action: 'show',
                  id: params.row.uuid,
                },
              })
            }
          }}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-cell': {
              // py: 2,
              display: 'flex',
              alignItems: 'center',
            },
          }}
        />
      </List>
    </>
  )
}
